<script setup lang="ts">
import OrderCard from "./OrderCard.vue";
import { computed, ref, watchEffect } from "vue";
import { currentUserLat, currentUserLng } from "@deliveryteam-spa-orders-vue/refs/UserCurrentLocation";
import { courierOrders, courierOrdersOnMap, getClosestOrderIdByRouteSorting } from "@deliveryteam-spa-orders-vue/composables/useMyOrdersMap";
import { DELIVERYTEAM_ORDER_STATUS_OBJ } from "@lib/types/deliveryteamOrderStatus";

const closestOrderId = computed(() => {
    // placed orders are always at the top of the list
    const orders = courierOrders.value.filter((order) => order.orderStatus === DELIVERYTEAM_ORDER_STATUS_OBJ.PLACED)
        .sort((a, b) => a.order_placed_at_ts - b.order_placed_at_ts);
    if (orders.length > 0) {
        return orders[0].deliveryteamOrderId;
    } else {
        // then get the closest order by route sorting, 
        // pickup point always first
        return getClosestOrderIdByRouteSorting(
            courierOrdersOnMap.value,
            currentUserLat.value,
            currentUserLng.value,
        )
    }
});

const closestOrder = computed(() =>
    closestOrderId.value
        ? courierOrders.value.find((order) => order.deliveryteamOrderId === closestOrderId.value) ?? null
        : null
);

const remainingOrders = computed(() =>
    closestOrderId.value
        ? courierOrders.value.filter((order) => order.deliveryteamOrderId !== closestOrderId.value)
        : courierOrders.value
);

/** Mirrors dock height into a CSS var so scroll content keeps clear space below (OrderCard buttons can grow). */
const CLOSEST_DOCK_CSS_VAR = "--closest-order-dock-h";
const dockOuterRef = ref<HTMLElement | null>(null);

watchEffect((onCleanup) => {
    const root = dockOuterRef.value;
    if (!root || !closestOrder.value) {
        document.documentElement.style.setProperty(CLOSEST_DOCK_CSS_VAR, "0px");
        return;
    }
    const sync = () => {
        document.documentElement.style.setProperty(
            CLOSEST_DOCK_CSS_VAR,
            `${Math.ceil(root.getBoundingClientRect().height)}px`,
        );
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(root);
    onCleanup(() => {
        ro.disconnect();
        document.documentElement.style.setProperty(CLOSEST_DOCK_CSS_VAR, "0px");
    });
});

</script>
<template>
    <div class="space-y-4">
        <Teleport to="body">
            <div ref="dockOuterRef"
                 v-if="closestOrder"
                 class="fixed bottom-0 left-0 right-0 z-[12] pointer-events-none">
                <div
                     class="pointer-events-auto mx-auto flex w-full max-w-2xl flex-col overflow-hidden max-h-[min(50dvh,40rem)]">
                    <div class="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
                        <!-- <div class="[&>*]:bg-card"> -->
                        <OrderCard :order="closestOrder" />
                        <!-- </div> -->
                    </div>
                </div>
            </div>
        </Teleport>
        <div v-for="order in remainingOrders"
             :key="order.deliveryteamOrderId"
             class="block">
            <OrderCard :order="order" />
        </div>
    </div>
</template>